import React, { useState, useEffect, useContext } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import {
  Menu,
  MenuItem,
  Button,
  CircularProgress,
  OutlinedInput,
} from "@material-ui/core";

import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import InfoOutlined from "@material-ui/icons/InfoOutlined";

import greyImagePlaceholder from "assets/images/image-uploader-placeholder.png";
import { ImageUploader } from "ui/pages/TemplatePreview/widgets";
import { WebpageCreationContext } from "../../../..";
import {
  MODAL_TYPES,
  FeaturesConfig,
  TEMPLATE_KEYS,
  SIDEBAR_SECTIONS,
  SIDEBAR_KEYS,
  SECTIONKEY_TO_MISC_KEY,
} from "../../../../constants";
import api from "data/APIs";

import style from "./style.module.css";
import baseStyle from "../../style.module.css";
import { IMAGE_SECTION_KEY_PATH } from "features/Common/modules/ExlyImage/constants/ExlyImage.constants";
import ExlyImage from "common/Components/ExlyImage";
import { is_empty } from "utils/validations";
import { creator_miscellaneous_data_types } from "features/Common/modules/CreatorData/CreatorData.constants";
import SectionVisibilityToggle from "../hideShow";
import { handleVisibilityToggle } from "../../utils";
import useVisibilityMutations from "../../useVisibilityMutations.hook";

const FeatureCard = (props) => {
  const {
    item,
    isDragging,
    innerRef,
    children,

    handleEdit,
    handleDelete,
    showImage,
    templateName,
    ...rest
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={style.featureCardOuter} ref={innerRef} {...rest}>
      <div
        className={`${style.featureCard} ${isDragging ? style.dragging : ""}${
          style?.[`${templateName}Template`]
        }`}
      >
        <div className={style.content}>
          {showImage ? (
            <ExlyImage
              src={item.media || greyImagePlaceholder}
              alt=""
              className={style.image}
            />
          ) : null}
          <div className={style.titleSection}>
            <div
              className={`${style.title} ${style?.[`${templateName}Template`]}`}
            >
              {item.title}
            </div>
            <div className={style.cardControls}>
              <div className={style.optionsMenu} onClick={handleOpen}>
                <MoreVertIcon />
              </div>
              {children}
            </div>
          </div>
          <div
            className={`${style.description} ${
              style?.[`${templateName}Template`]
            }`}
          >
            {item.description}
          </div>
          {TEMPLATE_KEYS.UNLOCK === templateName ? (
            <div className={style.ctaText}>{item.cta_text}</div>
          ) : null}
        </div>
      </div>
      <Menu
        id={`feature-menu-${item?.uuid}`}
        className={style.menuList}
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem
          onClick={() => {
            handleEdit();
            handleClose();
          }}
        >
          <EditIcon />
          &nbsp; Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDelete();
            handleClose();
          }}
        >
          <DeleteIcon />
          &nbsp; Delete
        </MenuItem>
      </Menu>
    </div>
  );
};

const FeatureList = ({
  stateProp = [],
  handleEdit,
  handleDelete,
  showImage,
  templateName,
  maxDataCount,
}) =>
  // slicing out the features list to the max allowed count to show in template
  (stateProp || []).slice(0, maxDataCount).map((item, index) => (
    <Draggable draggableId={item.uuid} index={index} key={item.uuid}>
      {(draggableProvided, draggableSnapshot) => (
        <FeatureCard
          key={item.uuid}
          item={item}
          handleEdit={handleEdit(item)}
          handleDelete={handleDelete(item)}
          isDragging={draggableSnapshot.isDragging}
          showImage={showImage}
          templateName={templateName}
          {...draggableProvided.draggableProps}
          innerRef={draggableProvided.innerRef}
        >
          <span
            className={style.dragHandle}
            {...draggableProvided.dragHandleProps}
            title="Drag and drop from here to reorder"
          >
            <DragIndicatorIcon />
          </span>
        </FeatureCard>
      )}
    </Draggable>
  ));

export const Features = (props) => {
  const { setField, state: userDataState, disableAllEdits } = props;

  const {
    fetchCall,
    pushNotif,
    templateData,
    features: { featuresData, setFeaturesData },
    modal: { openModal, closeModal },
    miscellaneous_data,
    fetchMiscellaneousData,
    set_miscellaneous_data,
  } = useContext(WebpageCreationContext);

  const [state, setState] = useState(featuresData || []);
  const [dragElement, setDragElement] = useState(null);
  const [isSaving, setSaving] = useState(null);
  const [visible, set_visible] = useState(true);

  // Mutations for visibility toggle
  const { createMutation, updateMutation } = useVisibilityMutations();

  const maxItemsCreated =
    state?.length >= (FeaturesConfig?.maxItems[templateData?.slug] || 3);

  const reorder = (list, { from: startIndex, to: endIndex }) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result.map((item, index) => ({
      ...item,
      priority: result.length - index - 1,
    }));
  };

  const handleDragStart = (result) => {
    setDragElement(result.draggableId);
    if (window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }
  };

  const handleDragEnd = (result) => {
    setDragElement(null);
    if (
      !result?.destination ||
      result?.destination.index === result?.source.index
    ) {
      return;
    }

    const newState = reorder(state, {
      from: result.source.index,
      to: result.destination.index,
    });

    setState(newState);
    setFeaturesData(newState);
    handleReorder(newState);
  };

  const handleReorder = (stateProp) => {
    fetchCall({
      url: api.reorder_feature,
      method: "POST",
      payload: {
        feature_items: stateProp.map((item, index) => ({
          priority: stateProp.length - index - 1,
          uuid: item.uuid,
        })),
      },
      errorCallback: () => {
        pushNotif("Error while updating feature order");
      },
    });
  };

  const handleCreate = () => {
    if (maxItemsCreated) {
      pushNotif(
        `You can only add upto ${
          FeaturesConfig.maxItems[templateData?.slug]
        } features`
      );
      return;
    }

    openModal(MODAL_TYPES.FEATURES, {
      modalLabels: {
        modalTitle: "Add new feature item",
        showDividers: true,
      },
      data: {},
      template: templateData.slug,
      primaryAction: ({ data: createData }) => {
        setSaving("Creating");
        closeModal();
        fetchCall({
          url: api.create_feature,
          method: "POST",
          payload: {
            feature_item: {
              ...createData,
            },
          },
          successCallback: ({ uuid }) => {
            pushNotif("Feature created");
            setFeaturesData([...featuresData, { ...createData, uuid }]);
            setSaving(null);
          },
          errorCallback: (err) => {
            pushNotif(err?.body?.message || "Error while creating feature");
            setSaving(null);
          },
        });
      },
      secondaryAction: closeModal,
    });
  };

  const handleEdit = (item) => () => {
    openModal(MODAL_TYPES.FEATURES, {
      modalLabels: {
        modalTitle: "Edit feature item",
        showDividers: true,
      },
      template: templateData.slug,
      data: item,
      primaryAction: ({ data: updatedData }) => {
        setSaving("Updating");
        closeModal();
        fetchCall({
          url: api.update_feature,
          method: "POST",
          payload: {
            uuid: item.uuid,
            feature_item: {
              ...updatedData,
            },
          },
          successCallback: () => {
            pushNotif("Feature Item updated");
            setFeaturesData([
              ...featuresData.map((t) =>
                t.uuid === item.uuid ? updatedData : t
              ),
            ]);
            setSaving(null);
          },
          errorCallback: (err) => {
            pushNotif(
              err?.body?.message || "Error while updating feature item"
            );
            setSaving(null);
          },
        });
      },
      secondaryAction: closeModal,
    });
  };

  const handleDelete = (item) => () => {
    openModal(MODAL_TYPES.CONFIRM, {
      modalLabels: {
        modalTitle: "Confirm Action",
        primaryText: "Cancel Delete",
        secondaryText: "Delete",
        showDividers: false,
      },
      body: `Are you sure you want to delete this feature item?`,
      hideCtaIcons: true,
      primaryAction: closeModal,
      secondaryAction: () => {
        setSaving("Deleting");
        closeModal();
        fetchCall({
          url: api.delete_feature,
          method: "POST",
          payload: {
            uuid: item.uuid,
          },
          successCallback: () => {
            pushNotif("Feature item deleted successfully!");
            setState((stateProp) =>
              stateProp.filter((t) => t.uuid !== item.uuid)
            );
            setFeaturesData(featuresData.filter((i) => i.uuid !== item.uuid));
            setSaving(null);
          },
          errorCallback: (error) => {
            pushNotif(
              error?.body?.message || "Error while deleting feature item"
            );
            setSaving(null);
          },
        });
      },
    });
  };

  useEffect(() => {
    setState(featuresData);
  }, [featuresData]);

  useEffect(() => {
    try {
      if (is_empty(miscellaneous_data?.topFeatures)) {
        set_visible(true);
        return;
      }
      const temp = miscellaneous_data.topFeatures[0];

      set_visible(
        typeof temp?.data?.visible === "boolean" ? temp.data.visible : true
      );
    } catch {
      set_visible(true);
    }
  }, [miscellaneous_data]);

  // Flags
  const showImage = FeaturesConfig.showImageInTemplates.includes(
    templateData.slug
  );

  const showSectionHeading = FeaturesConfig.showSectionHeading.includes(
    templateData.slug
  );

  if (!featuresData) {
    return <div>Loading...</div>;
  }

  const onToggleVisibility = () =>
    handleVisibilityToggle({
      currentVisible: visible,
      setVisible: set_visible,
      miscellaneousData: miscellaneous_data,
      sectionKey: SECTIONKEY_TO_MISC_KEY[SIDEBAR_KEYS.FEATURES],
      sectionType: creator_miscellaneous_data_types.TOP_FEATURES,
      defaultSectionName: SIDEBAR_SECTIONS[SIDEBAR_KEYS.FEATURES].title,
      fetchMiscellaneousData,
      set_miscellaneous_data,
      createMutation: createMutation,
      updateMutation: updateMutation,
    });

  return (
    <>
      <SectionVisibilityToggle
        visible={visible}
        disabled={disableAllEdits}
        onToggle={onToggleVisibility}
      />

      {showSectionHeading ? (
        <div className={baseStyle.fieldContainer}>
          <div className={baseStyle.label}>
            What would you like to call this section?
          </div>
          <div className={baseStyle.field}>
            <OutlinedInput
              placeholder="Enter the section heading here"
              className={baseStyle.input}
              rows={1}
              inputProps={{
                maxLength: FeaturesConfig.maxTitleLength[templateData?.slug],
              }}
              value={props?.state?.custom_headings?.features_title || ""}
              onChange={(e) => {
                setField("custom_headings", {
                  ...props?.state?.custom_headings,
                  features_title: e?.target?.value || "",
                });
              }}
            />
          </div>
          {props?.state?.custom_headings?.features_title?.length ===
          FeaturesConfig.maxTitleLength[templateData?.slug] ? (
            <div className={baseStyle.error}>
              Maximum field length of{" "}
              {FeaturesConfig.maxTitleLength[templateData?.slug]} reached.
              <br />
              <br />
            </div>
          ) : null}
        </div>
      ) : null}

      {templateData.slug === TEMPLATE_KEYS.EDUVERSE ? (
        <div className={baseStyle.fieldContainer}>
          <div className={baseStyle.label}>Description</div>
          <div className={baseStyle.field}>
            <OutlinedInput
              placeholder="Enter the section description here"
              className={baseStyle.input}
              multiline
              rows={5}
              inputProps={{
                maxLength: FeaturesConfig.maxDescLength[templateData?.slug],
              }}
              value={userDataState?.social_info?.features_description}
              onChange={(e) => {
                setField(
                  ["social_info", "features_description"],
                  e?.target?.value || ""
                );
              }}
            />
          </div>
          {userDataState?.social_info?.features_description?.length ===
          FeaturesConfig.maxDescLength[templateData?.slug] ? (
            <div className={baseStyle.error}>
              Maximum field length of{" "}
              {FeaturesConfig.maxDescLength[templateData?.slug]} reached.
              <br />
              <br />
            </div>
          ) : null}
        </div>
      ) : null}

      {templateData.slug === TEMPLATE_KEYS.YOGA ? (
        <div className={baseStyle.fieldContainer}>
          <div className={baseStyle.label}>Section Cover Image</div>
          <div className={baseStyle.field}>
            <ImageUploader
              uploadUrl={userDataState?.social_info?.features_image}
              setUploadUrl={(firebaseUrl) => {
                props.setField(["social_info", "features_image"], firebaseUrl);
              }}
              className={style.featureCoverImage}
              hoverText="Upload Image"
              fullWidth={250}
              width={250}
              height={250}
              borderRadius="2%"
              query={"cover"}
              id="feature_cover_image"
              uploadViaOptimization
              sectionName={IMAGE_SECTION_KEY_PATH.HOME_PAGE}
            />
          </div>
        </div>
      ) : null}

      <div className={`${baseStyle.info} !tw-my-[8px]`}>
        <InfoOutlined className={baseStyle.infoIcon} />
        The feature items can be dragged up and down using the
        <DragIndicatorIcon className={baseStyle.dragIcon} /> icon to change the
        order in which they appear.
      </div>
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Droppable droppableId="featureList">
          {(droppableProvided) => (
            <div
              className={`${style.featureContainer} ${
                dragElement ? style.dragHappening : ""
              }`}
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              <FeatureList
                stateProp={state}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                showImage={showImage}
                templateName={templateData.slug}
                maxDataCount={FeaturesConfig.maxItems[templateData?.slug]}
              />
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
        {isSaving ? (
          <div className={style.savingStatus}>
            {isSaving}&nbsp;
            <CircularProgress color="secondary" size={24} thickness={2} />
          </div>
        ) : null}
        <Button
          className={style.addButton}
          color="primary"
          disabled={isSaving}
          onClick={handleCreate}
          title={
            maxItemsCreated
              ? `You can only add upto ${
                  FeaturesConfig.maxItems[templateData?.slug]
                } features`
              : null
          }
        >
          <AddCircleIcon className={style.addIcon} /> Add new item
        </Button>
      </DragDropContext>
    </>
  );
};
